import { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from './components/Layout';
import { API_BASE_URL } from '../config/ApiConfig';
import { useNavigate } from 'react-router-dom';
import LoadingModal from './components/modals/LoadingModal';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import ConfirmModal from './components/modals/ConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import ResponsiveShadow from './components/ResponsiveShadow';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import ImagemModal from './components/modals/ImagemModal';
import './css/Imagem.css';
import DataTable from './components/DataTable';
import TableContainer from './components/TableContainer';

function Imagens() {
  const [imagens, setImagens] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [atualizar, setAtualizar] = useState(false);
  const [imagemSelecionada, setImagemSelecionada] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const token = localStorage.getItem('token');
  const [isLoading, setLoading] = useState(true);
  const navigate = useNavigate();
  const vinculo = JSON.parse(localStorage.getItem('vinculo'));
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();

  useEffect(() => {
    if (!token) {
      navigate('/');
      return;
    }
    if (!vinculo || !vinculo.idEntidade) {
      showDefaultModal('error', 'Selecione uma entidade antes de acessar esta página.');
      window.location.href = '/home';
      return;
    }
    
    setLoading(true);
    axios
      .get(`${API_BASE_URL.IMAGEM}/cnpj/${vinculo.idEntidade}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(result => {
        setImagens(result.data || []);
      })
      .catch(error => {
        console.error('Erro ao obter imagens:', error);
        console.error('Detalhes do erro:', error.response?.data || error.message);
        const errorMessage = error.response?.data?.message || error.message || 'Erro ao carregar imagens.';
        showDefaultModal('error', errorMessage);
        setImagens([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [atualizar]);

  function handleDelete(imagem) {
    showConfirmModal('delete', () => handleConfirmDelete(imagem));
  }

  function handleConfirmDelete(imagem) {
    axios
      .delete(`${API_BASE_URL.IMAGEM}/delete/${imagem.idEntidade}/${imagem.orientacao}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {
        showDefaultModal('success', 'Imagem excluída com sucesso!');
        setAtualizar(prev => !prev);
        setImagemSelecionada(null);
      })
      .catch(error => {
        console.error('Erro ao excluir imagem:', error);
        showDefaultModal('error', 'Erro ao excluir a imagem!');
      });
  }

  function handleEdit(imagem) {
    setImagemSelecionada(imagem);
    setShowModal(true);
  }

  function handleNew() {
    setImagemSelecionada(null);
    setShowModal(true);
  }

  function handleCloseModal() {
    setShowModal(false);
    setImagemSelecionada(null);
  }

  function handleSaveSuccess() {
    setAtualizar(prev => !prev);
    setImagemSelecionada(null);
  }

  const normalizeString = str => {
    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase();
  };

  const imagensFiltradas = Array.isArray(imagens)
    ? imagens.filter(img => {
        const normalizedBusca = normalizeString(filtro);
        return (
          normalizeString(img.orientacao || '').includes(normalizedBusca) ||
          normalizeString(img.idEntidade || '').includes(normalizedBusca)
        );
      })
    : [];

  const getOrientacaoLabel = (orientacao) => {
    return orientacao === 'R' ? 'Retrato' : orientacao === 'P' ? 'Paisagem' : orientacao;
  };

  const getImagePreview = (img) => {
    if (img && img.img) {
      return `data:image/png;base64,${img.img}`;
    }
    return null;
  };

  return (
    <Layout>
      <ResponsiveShadow>
        <PageHeader title="Imagens" icon="bi-image" />
        <PageContent>
          <div className="d-flex flex-column flex-lg-row justify-content-lg-between gap-2 mb-2 align-items-start align-items-lg-center">
            <div className="botao-grid-2col">
              <button
                className="btn btn-success btn-sm botao-igual"
                onClick={handleNew}
                title="Cadastrar nova imagem"
              >
                <i className="bi bi-plus-circle me-1"></i>
                Nova
              </button>
              <button
                className="btn btn-primary btn-sm botao-igual"
                disabled={!imagemSelecionada}
                onClick={() => handleEdit(imagemSelecionada)}
                title="Editar imagem selecionada"
              >
                <i className="bi bi-pencil me-1"></i>
                Editar
              </button>
              <button
                className="btn btn-danger btn-sm botao-igual"
                disabled={!imagemSelecionada}
                onClick={() => handleDelete(imagemSelecionada)}
                title="Excluir imagem selecionada"
              >
                <i className="bi bi-trash me-1"></i>
                Excluir
              </button>
            </div>
            <div className="mt-2 mt-lg-0">
              <div className="input-group input-group-sm">
                <span className="input-group-text">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Buscar por orientação..."
                  value={filtro}
                  onChange={e => setFiltro(e.target.value.toUpperCase())}
                />
              </div>
            </div>
          </div>

          <TableContainer isLoading={isLoading}>
              <DataTable style={{ fontSize: '0.8rem' }} head={
                <tr>
                  <th scope="col" className="px-2" style={{ width: '100px' }}>
                    Preview
                  </th>
                  <th scope="col" className="px-2">
                    Orientação
                  </th>
                  <th scope="col" className="px-2 d-none d-md-table-cell">
                    Tamanho
                  </th>
                </tr>
              }>
              <tbody>
                {!isLoading &&
                  imagensFiltradas.map((imagem, index) => (
                    <tr
                      key={`${imagem.idEntidade}-${imagem.orientacao}`}
                      className={
                        imagemSelecionada &&
                        imagemSelecionada.idEntidade === imagem.idEntidade &&
                        imagemSelecionada.orientacao === imagem.orientacao
                          ? 'table-primary'
                          : ''
                      }
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (
                          imagemSelecionada &&
                          imagemSelecionada.idEntidade === imagem.idEntidade &&
                          imagemSelecionada.orientacao === imagem.orientacao
                        ) {
                          setImagemSelecionada(null);
                        } else {
                          setImagemSelecionada(imagem);
                        }
                      }}
                    >
                      <td className="px-2">
                        {getImagePreview(imagem) ? (
                          <img
                            src={getImagePreview(imagem)}
                            alt="Preview"
                            style={{
                              maxWidth: '150px',
                              maxHeight: '150px',
                              objectFit: 'contain',
                            }}
                          />
                        ) : (
                          <i className="bi bi-image" style={{ fontSize: '2rem' }}></i>
                        )}
                      </td>
                      <td className="px-2">
                        {getOrientacaoLabel(imagem.orientacao)}
                      </td>
                      <td className="px-2 d-none d-md-table-cell">
                        {imagem.img ? `${(imagem.img.length * 0.75 / 1024).toFixed(2)} KB` : '-'}
                      </td>
                    </tr>
                  ))}
                {!isLoading && imagensFiltradas.length === 0 && (
                  <tr>
                    <td colSpan="4" className="text-center text-muted py-3">
                      Nenhuma imagem encontrada
                    </td>
                  </tr>
                )}
              </tbody>
            </DataTable>
          </TableContainer>

          <ConfirmModal {...setConfirmModal} />
          <DefaultModal {...setDefaultModal} />
        </PageContent>
      </ResponsiveShadow>

      {showModal && (
        <ImagemModal
          show={showModal}
          imagem={imagemSelecionada}
          onClose={handleCloseModal}
          onSave={handleSaveSuccess}
        />
      )}
    </Layout>
  );
}

export default Imagens;
